import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { TICKET_LIST_ACTION_TYPES } from '@constants/actionTypes/tikcetListActionTypes';
import { API_STATUS_CODES, defaultPageInfo } from '@constants/common.constants';
import { configs } from '@constants/configs/configs';
import { useAppDispatch } from '@hooks/index';
import { ROOT_STATE } from '@store/store.types';
import {
    DEFAULT_PAYLOAD,
    SearchType,
    TICKET_LIST_PAGE_INFO,
    TICKET_LIST_SEARCH_FROM_REPORTS,
    defaultOption,
} from './constants/ticketlist.constants';
import {
    formatFilterValue,
    getItemFromSessionStorage,
    getTicketsSessionData,
    getViewDetailUrl,
    processTicketData,
    setItemToSessionStorage,
} from './constants/ticketlist.utils';
import { useApiHandler } from './hooks/useApiHandler';

export const useTicketListContainer = () => {
    const { defaultListAPI, controllerAPI, claimNewTicketAPI, filteredListAPI } = useApiHandler();
    const dispatch = useAppDispatch();
    const isInitialLoad = useRef(true);
    const [selectedSearchType, setSelectedSearchType] = useState(SearchType.DEFAULT);
    const [isReportPageRedirect, setIsReportPageRedirect] = useState(true);
    const [resetTrigger, setResetTrigger] = useState(false);

    const ticketsData = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList.tickets) || {};
    const tickets = ticketsData.ticketListJson || [];
    const searchCriteria = ticketsData.searchCriteriaJson || {};
    const ticketsCount = ticketsData.count || 0;

    const ticketController = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList.controller) || {};
    const environment = useSelector((state: ROOT_STATE) => state.common.environment) || {};
    const {
        viewTicketPermission = true,
        isWatchTicketAllowed = true,
        isManageWatcherAllowed = true,
        capTicketEnabled: enableTicketSupport = true,
    } = environment;
    const userticketQueues =
        useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList.controller.userticketQueues) || [];

    const {
        isOpen: isErrorModalOpen,
        errorMessage,
        modalTitle,
    } = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList.errorModalData);
    const isAdvanceSearch = selectedSearchType === SearchType.ADVANCE;

    useEffect(() => {
        if (isInitialLoad.current) {
            fetchIntialController();
            setItemToSessionStorage(TICKET_LIST_PAGE_INFO, { ...defaultPageInfo, sortField: '', sortOrder: '' });
            if (window.location.search === '?dueDateReport=true') {
                setIsReportPageRedirect(false);
                const reportTicketDashboard = getItemFromSessionStorage(TICKET_LIST_SEARCH_FROM_REPORTS);
                if (reportTicketDashboard) {
                    const payload = {
                        ...reportTicketDashboard,
                        csrftoken: window.csrf_token,
                    };
                    filteredListAPI(dispatch, payload);
                }
            } else {
                fetchDefaultTickets();
            }
            isInitialLoad.current = false;
        }
    }, []);

    const modifiedWorkgroupOptions = [
        ...defaultOption,
        ...userticketQueues.map(queue => ({ value: queue.name, text: queue.name })),
    ];

    const onClaimNewTicket = async (workgroup: string = 'Any') => {
        const payload = { workGroup: workgroup, csrftoken: window.csrf_token };
        const { data, statusCode } = (await claimNewTicketAPI(dispatch, payload)) || {};
        if (statusCode === API_STATUS_CODES.OK) {
            window.location.href = getViewDetailUrl(data);
        }
    };

    const onAddNewTicket = async () => {
        const url = new URL(configs.endpoints.TICKET_MGMT_CREATE_PAGE, window.location.origin);
        window.location.href = url.toString();
    };

    const fetchDefaultTickets = () => {
        const payload = { ...DEFAULT_PAYLOAD, csrftoken: window.csrf_token };
        defaultListAPI(dispatch, payload);
    };

    const fetchIntialController = async () => {
        await controllerAPI(dispatch, {});
    };

    const onErrorModalClose = () => {
        dispatch({
            type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
            payload: { isOpen: false, errorMessage: '', modalTitle: '' },
        });
    };

    const fetchFilteredTickets = async () => {
        const data = getTicketsSessionData();
        const payload = {
            ...DEFAULT_PAYLOAD,
            filterticketStatus: formatFilterValue(data?.filterticketStatus),
            userticketQueues: formatFilterValue(data?.userticketQueues),
            userFilter: data?.userFilter || '',
            csrftoken: window.csrf_token,
        };
        await filteredListAPI(dispatch, payload);
    };

    const getProcessedTickets = useMemo(() => {
        return processTicketData(tickets, ticketController);
    }, [JSON.stringify(tickets), JSON.stringify(ticketController)]);

    return {
        fetchDefaultTickets,
        setSelectedSearchType,
        isAdvanceSearch,
        tickets: getProcessedTickets,
        searchCriteria,
        ticketsCount,
        isInitialLoad,
        onClaimNewTicket,
        onAddNewTicket,
        workgroupOptions: modifiedWorkgroupOptions,
        onErrorModalClose,
        isErrorModalOpen,
        errorMessage,
        modalTitle,
        enableTicketSupport,
        viewTicketPermission,
        isReportPageRedirect,
        fetchFilteredTickets,
        resetTrigger,
        setResetTrigger,
        isWatchTicketAllowed,
        isManageWatcherAllowed,
    };
};
